import type { TFunction } from 'next-i18next';
import { LOCALE_SEARCH_PATHS } from 'types/locales';
import { getPriceRange, getSqftRange, getListingStatus } from 'components/modal-dialog/saved-search-filters/search-criteria';
import type { SavedSearchListingStatus } from 'components/modal-dialog/saved-search-filters/search-criteria';
import type { PropertyType } from 'contexts/preferences/listing-params/types';
import type { SearchSummary, SearchAmenityKey } from 'domain/saved-search';

/** Home-type key → the reused `search:filter.property_types.*` path + English fallback. */
const HOME_TYPE: Record<PropertyType, { path: string; label: string }> = {
  house: { path: LOCALE_SEARCH_PATHS.FILTER_PROPERTY_TYPES_HOUSE, label: 'House' },
  townhouse: { path: LOCALE_SEARCH_PATHS.FILTER_PROPERTY_TYPES_TOWNHOUSE, label: 'Townhouse' },
  condo: { path: LOCALE_SEARCH_PATHS.FILTER_PROPERTY_TYPES_CONDO, label: 'Condo' },
  land: { path: LOCALE_SEARCH_PATHS.FILTER_PROPERTY_TYPES_LAND, label: 'Land' },
  commercial: { path: LOCALE_SEARCH_PATHS.FILTER_PROPERTY_TYPES_COMMERCIAL, label: 'Commercial' },
  farm: { path: LOCALE_SEARCH_PATHS.FILTER_PROPERTY_TYPES_FARM, label: 'Farm' },
};

/** Amenity key → the reused `search:filter.chip.*` path + English fallback. */
const AMENITY: Record<SearchAmenityKey, { path: string; label: string }> = {
  openHouse: { path: LOCALE_SEARCH_PATHS.FILTER_CHIP_OPEN_HOUSE, label: 'Open House' },
  garage: { path: LOCALE_SEARCH_PATHS.FILTER_CHIP_GARAGE, label: 'Garage' },
  pool: { path: LOCALE_SEARCH_PATHS.FILTER_CHIP_POOL, label: 'Pool' },
  fireplace: { path: LOCALE_SEARCH_PATHS.FILTER_CHIP_FIREPLACE, label: 'Fireplace' },
  waterfront: { path: LOCALE_SEARCH_PATHS.FILTER_CHIP_WATERFRONT, label: 'Waterfront' },
};

/** Listing status → the reused `search:filter.status.*` path. */
const STATUS_PATH: Record<SavedSearchListingStatus, string> = {
  Active: LOCALE_SEARCH_PATHS.FILTER_STATUS_ACTIVE,
  Leased: LOCALE_SEARCH_PATHS.FILTER_STATUS_LEASED,
  Sold: LOCALE_SEARCH_PATHS.FILTER_STATUS_SOLD,
  Expired: LOCALE_SEARCH_PATHS.FILTER_STATUS_EXPIRED,
  Pending: LOCALE_SEARCH_PATHS.FILTER_STATUS_PENDING,
};

/**
 * Formats a saved search's `SearchSummary` into a card subtitle, e.g.
 * "Annex · House · 2+ Beds · 1+ Baths · $500,000+".
 *
 * The domain-to-presentation bridge: the domain (`SearchSummary`) decides which
 * facets are meaningful; this ui-layer function turns them into localized labels
 * and joins them. Takes a `t` (from the calling page's `useTranslation`) and
 * reuses the existing `search:filter.*` label keys rather than duplicating them.
 * Price and sqft ranges are left to the shared search-criteria helpers.
 */
export function formatSearchSummary(summary: SearchSummary, t: TFunction): string {
  const parts: string[] = [];

  if (summary.areaName) {
    parts.push(summary.areaName);
  }
  if (summary.rental) {
    parts.push(t(LOCALE_SEARCH_PATHS.FILTER_CHIP_FOR_RENT, { defaultValue: 'For Rent' }));
  }
  if (summary.homeTypes.length > 0) {
    parts.push(summary.homeTypes.map(key => t(HOME_TYPE[key].path, { defaultValue: HOME_TYPE[key].label })).join(', '));
  }
  if (summary.bedrooms) {
    parts.push(t(LOCALE_SEARCH_PATHS.FILTER_CHIP_BEDS_FORMAT, { bedrooms: summary.bedrooms, defaultValue: `${summary.bedrooms} Beds` }));
  }
  if (summary.bathrooms) {
    parts.push(t(LOCALE_SEARCH_PATHS.FILTER_CHIP_BATHS_FORMAT, { bathrooms: summary.bathrooms, defaultValue: `${summary.bathrooms} Baths` }));
  }
  if (summary.parkingSpaces) {
    parts.push(t(LOCALE_SEARCH_PATHS.FILTER_CHIP_PARKING_FORMAT, { spaces: summary.parkingSpaces, defaultValue: `${summary.parkingSpaces} Parking` }));
  }
  if (summary.price) {
    parts.push(getPriceRange(summary.price.min ?? 0, summary.price.max ?? 0, summary.rental));
  }
  if (summary.sqft) {
    const sqft = getSqftRange(summary.sqft.min ?? 0, summary.sqft.max ?? 0);
    if (sqft) {
      parts.push(sqft);
    }
  }
  if (summary.status) {
    const label = getListingStatus(summary.status, summary.rental);
    parts.push(t(STATUS_PATH[label], { suffix: '', defaultValue: label }));
  }
  summary.amenities.forEach(amenity => {
    const { path, label } = AMENITY[amenity];
    parts.push(t(path, { defaultValue: label }));
  });
  if (summary.locker) {
    parts.push(t(LOCALE_SEARCH_PATHS.FILTER_CHIP_LOCKER, { defaultValue: 'Locker' }));
  }
  if (summary.maintenanceFee) {
    parts.push(t(LOCALE_SEARCH_PATHS.FILTER_CHIP_MAX_MAINTENANCE_FEE_FORMAT, { fee: summary.maintenanceFee, defaultValue: `Max $${summary.maintenanceFee} Maintenance Fee` }));
  }

  return parts.join(' · ');
}
